import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Scale, Save, Info } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { ROLES } from "../../lib/roles";

const emptyEscalon = {
    nombre: "",
    monto_min: 0,
    monto_max: 0,
    firmas_requeridas: 1,
    roles_permitidos: ["delegado_fiduciario"],
    requiere_call_back: false,
    requiere_visto_bueno: [],
};

export default function MatrizAutorizacion() {
    const { hasRole } = useAuth();
    const canEdit = hasRole([ROLES.DIRECCION, ROLES.ADMIN_TI]);
    const [escalones, setEscalones] = useState([]);
    const [meta, setMeta] = useState({});
    const [loading, setLoading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/gobierno/matriz-autorizacion");
            setEscalones(data.escalones || []);
            setMeta({ updated_at: data.updated_at, updated_by: data.updated_by });
        } catch (err) {
            toast.error("No se pudo cargar la matriz", {
                description: err?.response?.data?.detail,
            });
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const updateEscalon = (idx, field, value) => {
        const next = [...escalones];
        next[idx] = { ...next[idx], [field]: value };
        setEscalones(next);
    };

    const guardar = async () => {
        try {
            await api.put("/gobierno/matriz-autorizacion", { escalones });
            toast.success("Matriz actualizada");
            load();
        } catch (err) {
            toast.error("No se pudo guardar", {
                description: err?.response?.data?.detail,
            });
        }
    };

    return (
        <div className="p-8 lg:p-10 space-y-6 max-w-[1500px]" data-testid="matriz-page">
            <div className="flex items-start justify-between gap-6 flex-wrap">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                        Gobierno · Fase 14
                    </div>
                    <h1 className="font-display text-4xl tracking-tight flex items-center gap-3">
                        <Scale className="h-8 w-8 text-[color:var(--brand-dark-gold)]" />
                        Matriz de Autorización
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        Escalones por monto en MXN. Cada instrucción se evalúa contra esta matriz
                        para determinar firmas requeridas, call-back y visto bueno de Dirección.
                    </p>
                </div>
                {canEdit && (
                    <Button onClick={guardar} data-testid="save-matriz">
                        <Save className="h-3.5 w-3.5 mr-2" /> Guardar cambios
                    </Button>
                )}
            </div>

            {meta.updated_by && (
                <div className="text-xs text-muted-foreground flex items-center gap-2">
                    <Info className="h-3 w-3" /> Última actualización por{" "}
                    <strong>{meta.updated_by}</strong> · {meta.updated_at?.slice(0, 19).replace("T", " ")}
                </div>
            )}

            <div className="rounded-md border border-border overflow-hidden bg-card">
                <table className="trf-table" data-testid="matriz-table">
                    <thead>
                        <tr>
                            <th>Nombre</th>
                            <th className="text-right">Monto mín (MXN)</th>
                            <th className="text-right">Monto máx (MXN)</th>
                            <th className="text-center">Firmas requeridas</th>
                            <th className="text-center">Call-back</th>
                            <th className="text-center">VB Dirección</th>
                        </tr>
                    </thead>
                    <tbody>
                        {escalones.map((e, idx) => (
                            <tr key={idx} data-testid={`escalon-row-${idx}`}>
                                <td>
                                    <Input
                                        value={e.nombre}
                                        onChange={(ev) => updateEscalon(idx, "nombre", ev.target.value)}
                                        disabled={!canEdit}
                                        data-testid={`escalon-nombre-${idx}`}
                                        className="max-w-[180px]"
                                    />
                                </td>
                                <td>
                                    <Input
                                        type="number"
                                        value={e.monto_min}
                                        onChange={(ev) =>
                                            updateEscalon(idx, "monto_min", Number(ev.target.value))
                                        }
                                        disabled={!canEdit}
                                        className="text-right max-w-[160px] ml-auto tabular"
                                    />
                                </td>
                                <td>
                                    <Input
                                        type="number"
                                        value={e.monto_max ?? ""}
                                        onChange={(ev) =>
                                            updateEscalon(
                                                idx,
                                                "monto_max",
                                                ev.target.value === "" ? null : Number(ev.target.value),
                                            )
                                        }
                                        disabled={!canEdit}
                                        placeholder="∞"
                                        className="text-right max-w-[160px] ml-auto tabular"
                                    />
                                </td>
                                <td className="text-center">
                                    <Input
                                        type="number"
                                        min={1}
                                        value={e.firmas_requeridas}
                                        onChange={(ev) =>
                                            updateEscalon(idx, "firmas_requeridas", Number(ev.target.value))
                                        }
                                        disabled={!canEdit}
                                        className="text-center max-w-[80px] mx-auto"
                                    />
                                </td>
                                <td className="text-center">
                                    <input
                                        type="checkbox"
                                        checked={!!e.requiere_call_back}
                                        onChange={(ev) =>
                                            updateEscalon(idx, "requiere_call_back", ev.target.checked)
                                        }
                                        disabled={!canEdit}
                                        data-testid={`escalon-callback-${idx}`}
                                        className="h-4 w-4"
                                    />
                                </td>
                                <td className="text-center">
                                    <input
                                        type="checkbox"
                                        checked={(e.requiere_visto_bueno || []).includes("direccion")}
                                        onChange={(ev) =>
                                            updateEscalon(
                                                idx,
                                                "requiere_visto_bueno",
                                                ev.target.checked ? ["direccion"] : [],
                                            )
                                        }
                                        disabled={!canEdit}
                                        data-testid={`escalon-vbdg-${idx}`}
                                        className="h-4 w-4"
                                    />
                                </td>
                            </tr>
                        ))}
                        {escalones.length === 0 && !loading && (
                            <tr>
                                <td colSpan={6} className="text-center py-10 text-muted-foreground">
                                    Sin escalones configurados.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <div className="kpi-card bg-amber-50/50 border-amber-200">
                <div className="text-xs uppercase tracking-widest text-amber-800 font-semibold mb-2">
                    Reglas críticas
                </div>
                <ul className="text-sm space-y-1 text-amber-900">
                    <li>· Los escalones no pueden traslaparse (validación server-side).</li>
                    <li>· Sólo el último escalón puede tener <code>monto_max = ∞</code>.</li>
                    <li>· Instrucciones con monto <strong>DEBEN</strong> satisfacer la matriz antes de ser enviadas.</li>
                    <li>· Los delegados suspendidos/revocados no pueden firmar (bloqueo hard).</li>
                </ul>
            </div>
        </div>
    );
}
